import { Injectable } from '@angular/core';
import { Integration, IntegrationType, Recipe } from '@pdqdev/pdq-shared/dist/entities';
import { DynamicRecipeModels } from '@pdqdev/pdq-shared/dist/interfaces';

import { BehaviorSubject, Observable } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';
import { ApiService } from '../../core/services/api/api.service';
import { IntegrationsMap } from './integration.model';

@Injectable({
  providedIn: 'root',
})
export class IntegrationsService {
  private _integrations$: BehaviorSubject<IntegrationsMap>;

  private static readonly SETTINGS_SUPPORTING_INTEGRATION_NAMES = [
    'gorgias',
    'alloy_klaviyo', // keeps the Settings tab (the "provide tags" toggle) on the Klaviyo tile
    'alloy_gladly',
    'alloy_attentive',
    'richpanel',
    'alloy_zendesk',
    'triple_whale',
    'okendo',
    'yotpo',
    'judge_me',
    'bazaarvoice',
    'order_protection',
  ];
  constructor(private apiService: ApiService) {
    this.initIntegrations();
  }

  get integrations$(): Observable<IntegrationsMap> {
    return this._integrations$.asObservable();
  }

  /**
   * @method createIntegration: First step of connection
   *  creates the initial instances that required for the integration
   */
  createIntegration(integrationTypeName: string): Observable<Integration> {
    return new Observable((subscriber) => {
      this.apiService
        .post(`integrations/${integrationTypeName}`)
        .pipe(first())
        .subscribe(
          (integration: Integration) => {
            this.updateIntegrationState(integration);
            subscriber.next(integration);
          },
          (err) => {
            subscriber.error(err);
          },
        );
    });
  }
  /**
   * @method toggleIntegration:
   * When @param newIsConnected is true, modifies/sets configuration of the integration that created in step-1
   * when @param newIsConnected is false, sets the integration to be non active.
   */
  toggleConnection(integration: Integration, newIsConnected: boolean, config: any): Observable<Integration> {
    return new Observable((subscriber) => {
      if (!integration) {
        subscriber.error('Integration instance parameter missing');
      }
      this.apiService
        .patch(`integrations/toggle-connection/${integration.id}?newIsConnected=${newIsConnected}`, config)
        .pipe(first())
        .subscribe(
          (integration: Integration) => {
            this.updateIntegrationState(integration);
            subscriber.next(integration);
          },
          (err) => subscriber.error(err),
        );
    });
  }

  /** Refetch all integrations and push the fresh state — call after an out-of-band connection change. */
  refreshIntegrations(): Observable<IntegrationsMap> {
    return this.apiService.get('integrations').pipe(
      first(),
      tap((integrations: IntegrationsMap) => this._integrations$.next(integrations)),
    );
  }

  isConnected(integrationTypeName: string) {
    return this._integrations$.getValue()?.[integrationTypeName]?.instance?.isConnected;
  }

  static hasSettings(integrationTypeName: string) {
    return IntegrationsService.SETTINGS_SUPPORTING_INTEGRATION_NAMES.includes(integrationTypeName);
  }

  toggleRecipe(recipe: Recipe, newIsActive: boolean, integrationType: IntegrationType) {
    return new Observable((subscriber) => {
      if (!integrationType?.name || !recipe?.id) {
        subscriber.error('Integration type or recipe id is missing');
      }
      this.apiService.patch(`integrations/recipes/toggle/${recipe.id}?newIsActive=${newIsActive}`).subscribe(
        (updatedRecipe: Recipe) => {
          this.updateRecipeState(updatedRecipe, integrationType.name);
          subscriber.next(updatedRecipe);
        },
        (err) => subscriber.next(err),
      );
    }).pipe(first());
  }

  static isAlloy(integrationType: IntegrationType) {
    return integrationType.name.includes('alloy');
  }

  deleteRecipe(recipe: Recipe, integrationType: IntegrationType) {
    return new Observable((subscriber) => {
      const integration = this._integrations$.getValue()[integrationType.name];
      if (!recipe?.id) {
        subscriber.error('Recipe id is missing');
      }
      if (!integration?.instance?.isConnected) {
        subscriber.error('Active integration not found');
      }

      this.apiService.delete(`integrations/recipes/${recipe.id}`, { ...recipe }).subscribe(
        () => {
          this.updateRecipeState(recipe, integration.type.name, true);
          subscriber.next(true);
        },
        (err) => subscriber.error(err),
      );
    }).pipe(first());
  }

  postRecipe(recipe: Recipe, integrationType: IntegrationType): Observable<Recipe> {
    return new Observable((subscriber) => {
      const integration = this._integrations$.getValue()[integrationType.name];
      if (!integration?.instance?.isConnected) {
        subscriber.error('Active integration not found');
      }
      this.apiService.post(`integrations/recipes/${integration.instance.id}`, { ...recipe }).subscribe(
        (updatedRecipe: Recipe) => {
          this.updateRecipeState(updatedRecipe, integration.type.name);
          subscriber.next(updatedRecipe);
        },
        (err) => subscriber.error(err),
      );
    }).pipe(first()) as Observable<Recipe>;
  }

  private updateRecipeState(recipe: Recipe, integrationKey: string, deleteThisRecipe?: boolean) {
    const integrations = this._integrations$.getValue();
    const integration = integrations[integrationKey];
    const recipes = [...integration.instance.recipes];
    const existingRecipeIdx = recipes.findIndex((recipeItem) => recipeItem.id === recipe.id);
    if (existingRecipeIdx !== -1) {
      if (deleteThisRecipe) {
        recipes.splice(existingRecipeIdx, 1);
      } else {
        const oldRecipe = recipes[existingRecipeIdx];
        recipes[existingRecipeIdx] = {
          ...oldRecipe,
          ...recipe,
        };
      }
    } else if (!deleteThisRecipe) {
      recipes.push(recipe);
    }
    integration.instance.recipes = recipes;
    integrations[integrationKey] = integration;
    this._integrations$.next(integrations);
  }

  updateIntegrationState(integration: Integration) {
    const integrationKey = integration.type.name;
    const currentState = this._integrations$.getValue();
    const newState = { ...currentState };
    const currentIntegrationState = currentState[integrationKey]?.instance;
    const recipes = integration.recipes || currentIntegrationState.recipes;
    const type = integration.type || currentIntegrationState.type;
    const shop = integration.shop || currentIntegrationState.shop;
    const metadata = integration.metadata || currentState.metadata;
    const newIntegrationState = { ...integration, recipes, type, shop, metadata };

    newState[integrationKey].instance = newIntegrationState;

    this._integrations$.next(newState);
  }

  static getLogoUrl(integrationTypeName: string) {
    return `/assets/images/integrations/${integrationTypeName}.svg`;
  }

  static getMiniLogoUrl(integrationTypeName: string) {
    return `/assets/images/integrations/${integrationTypeName}_mini.svg`;
  }

  getAlloyUserToken(): Observable<{ token: string }> {
    return this.apiService.get('integrations/alloy-token');
  }

  getDirectOAuthUrl(slug: string): Observable<{ url: string }> {
    return this.apiService.get(`integrations/direct/${slug}/oauth/url`);
  }

  disconnectDirect(slug: string): Observable<void> {
    return this.apiService.patch(`integrations/direct/${slug}/disconnect`).pipe(
      first(),
      // Reconcile from a fresh fetch BEFORE completing (mirrors toggleConnection): the caller's success
      // handler then runs against up-to-date state, and a refetch failure surfaces as an error to the
      // caller instead of being swallowed by a detached subscription.
      switchMap(() => this.apiService.get('integrations').pipe(first())),
      tap((integrations: IntegrationsMap) => this._integrations$.next(integrations)),
      map(() => void 0),
    );
  }
  static getVariableValues(variable: DynamicRecipeModels.Variable) {
    return (
      variable.type === DynamicRecipeModels.InputType.MULTI_SELECT
        ? (variable.inputConfig as DynamicRecipeModels.MultiSelectInputConfig).selectedValues
        : [(variable.inputConfig as DynamicRecipeModels.SelectInputConfig).selectedValue]
    ).filter(Boolean);
  }
  private initIntegrations() {
    this._integrations$ = new BehaviorSubject({});
    this.apiService
      .get('integrations')
      .pipe(first())
      .subscribe((integrations: IntegrationsMap) => {
        this._integrations$.next(integrations);
      });
  }
}
